import React, { createContext, useContext, useEffect,useState } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Initialize language from localStorage or browser preference
  useEffect(() => {
    const initializeLanguage = async () => {
      const savedLanguage = localStorage.getItem('language');
      let selectedLanguage = 'en';
      
      if (savedLanguage) {
        selectedLanguage = savedLanguage;
      } else {
        // Detect browser language
        const browserLanguage = navigator.language.split('-')[0];
        if (browserLanguage === 'es' || browserLanguage === 'en') {
          selectedLanguage = browserLanguage;
        }
      }
      
      setLanguage(selectedLanguage);
      await loadTranslations(selectedLanguage);
      setIsLoading(false);
    };

    initializeLanguage();
  }, []);

  // Load translations when language changes (but not on initial load)
  useEffect(() => {
    if (!isLoading && language) {
      loadTranslations(language);
    }
  }, [language, isLoading]);

  const loadTranslations = async (lang) => {
    try {
      setIsLoading(true);
      const translationModule = await import(`../translations/${lang}.js`);
      setTranslations(translationModule.default);
    } catch (error) {
      console.error('Error loading translations:', error);
      // Fallback to English
      try {
        const fallbackModule = await import('../translations/en.js');
        setTranslations(fallbackModule.default);
      } catch (fallbackError) {
        console.error('Error loading fallback translations:', fallbackError);
        setTranslations({});
      }
    } finally {
      setIsLoading(false);
    }
  };

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const t = (key, params = {}) => {
    // Return empty string while loading to prevent showing keys
    if (isLoading) {
      return '';
    }

    const keys = key.split('.');
    let translation = translations;
    
    for (const k of keys) {
      translation = translation?.[k];
    }
    
    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    
    // Replace parameters in translation only if translation is a string
    if (typeof translation === 'string') {
      let result = translation;
      Object.keys(params).forEach(param => {
        result = result.replace(`{{${param}}}`, params[param]);
      });
      return result;
    }
    // If translation is array/object, return as is (for returnObjects: true)
    return translation;
  };

  const value = {
    language,
    changeLanguage,
    t,
    translations,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
